/**
 * Live Trade Execution & Order Management (Phase 4) service — talks to
 * `/api/v1/execution/*` (see `app.api.v1.execution`).
 *
 * EXECUTION SAFETY: `preview` and `confirm` never place a broker order —
 * see backend `ExecutionService`'s own module docstring for the full
 * guarantee. `place` is the ONLY method in this entire frontend that can
 * result in a real broker order, and only after `ExecutionPanel.tsx`'s own
 * mandatory confirmation checkbox has been checked. Every method here is
 * implicitly scoped to the current authenticated user by the backend (via
 * the JWT).
 */

import api from "@/services/api";
import type {
  BrokerOrderRead,
  CancelOrderRequest,
  ConfirmExecutionRead,
  ConfirmExecutionRequest,
  ExecutionEventListRead,
  ExecutionResultRead,
  ExecutionStatusRead,
  LiveExecutionPreviewRead,
  LiveExecutionPreviewRequest,
  PlaceOrdersRequest,
} from "@/types";

export const executionService = {
  async preview(symbol: string, payload: LiveExecutionPreviewRequest): Promise<LiveExecutionPreviewRead> {
    const { data } = await api.post<LiveExecutionPreviewRead>(`/execution/${symbol}/preview`, payload);
    return data;
  },

  async confirm(payload: ConfirmExecutionRequest): Promise<ConfirmExecutionRead> {
    const { data } = await api.post<ConfirmExecutionRead>("/execution/confirm", payload);
    return data;
  },

  async place(payload: PlaceOrdersRequest): Promise<ExecutionStatusRead> {
    const { data } = await api.post<ExecutionStatusRead>("/execution/place", payload);
    return data;
  },

  async getStatus(executionId: string): Promise<ExecutionStatusRead> {
    const { data } = await api.get<ExecutionStatusRead>(`/execution/${executionId}`);
    return data;
  },

  async listOrders(executionId: string): Promise<BrokerOrderRead[]> {
    const { data } = await api.get<BrokerOrderRead[]>(`/execution/${executionId}/orders`);
    return data;
  },

  async getResult(executionId: string): Promise<ExecutionResultRead> {
    const { data } = await api.get<ExecutionResultRead>(`/execution/${executionId}/result`);
    return data;
  },

  async listEvents(executionId: string): Promise<ExecutionEventListRead> {
    const { data } = await api.get<ExecutionEventListRead>(`/execution/${executionId}/events`);
    return data;
  },

  async cancelOrder(executionId: string, payload: CancelOrderRequest): Promise<BrokerOrderRead> {
    const { data } = await api.post<BrokerOrderRead>(`/execution/${executionId}/cancel-order`, payload);
    return data;
  },
};

export default executionService;
